import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AddMemberToSample from '@components/assign-team-member/AddMemberToSample';
import { toastSuccess } from '@components/Toast';
import TopDetails from '@components/TopDetails';

import CreateSetOrder from '@features/collection/details/CreateSetOrder';
import DesignCard from '@features/collection/details/DesignCard';
import SelectFolderPopup from '@features/favourites/components/SelectFolderPopup';
import SampleActionPopup from '@features/sampleDevelopment/components/SampleActionPopup';

import { storeProductData } from '@store/actions/DesignAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchSampleProductById } from '@store/actions/SampleAction';
import { likeProduct } from '@store/design/action';

import { fetchAllProductTags, getSetInfo, getSetProducts } from '@services/collectionService';

import { changeDateFormat, checkSingular, getUserPreference, isShowPriceOnDesign } from '@utils/Util';

const SetOrderDetails = () => {
	const selectedProductIds = useSelector((store) => store.product.selectedProductIds);
	const [isOpenSetOrder, setIsOpenSetOrder] = useState(false);
	const [productWiseTags, setProductWiseTags] = useState([]);
	const [setOrderInfo, setSetOrderInfo] = useState({});
	const [productList, setProductList] = useState([]);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();
	const location = useLocation();
	const [requestedDepartment, setRequestedSampleDepartment] = useState('');
	const [selectedProduct, setSelectedProduct] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const sampleViewStore = useSelector((store) => store.sampleViewStore);
	const [sampleRequestId, setSampleRequestId] = useState(null);
	const [initialDataLoaded, setInitialDataLoaded] = useState(false);
	const [sampleInfo, setSampleInfo] = useState({});
	let previousCancel;
	const [currentLikedStyle, setCurrentLikedStyle] = useState(null);
	const userPreference = getUserPreference();
	const [, setDefaultFolder] = useState(null);
	const [isShowFavouriteFolderPopup, setIsShowFavouriteFolderPopup] = useState(false);
	const [isShowCreateNew, setIsShowCreateNew] = useState(false);

	useEffect(() => {
		setDefaultFolder(userPreference?.DEFAULT_FAVOURITE_FOLDER?.id || -1);
	}, [userPreference?.DEFAULT_FAVOURITE_FOLDER?.id]);

	const fetchSampleDetails = (sampleRequestId) => {
		dispatch(openLoader());
		dispatch(fetchSampleProductById(sampleRequestId)).finally(() => dispatch(closeLoader()));
	};

	useEffect(() => {
		const requestQuery = new URLSearchParams(location.search).get('department');
		const sampleId = new URLSearchParams(location.search).get('sampleRequestId');
		setSampleRequestId(sampleId);
		setRequestedSampleDepartment(requestQuery);
		setInitialDataLoaded(true);
	}, [location.search]);

	useEffect(() => {
		if (sampleRequestId) fetchSampleDetails(sampleRequestId);
	}, [sampleRequestId]);

	useEffect(() => {
		const sampleResponse = sampleViewStore?.response?.sampleRequestItemResponseList;
		if (sampleResponse && sampleResponse.length > 0) {
			const getProduct = sampleResponse.filter((item) => {
				return parseInt(item.productId) === parseInt(id);
			});

			const formattedList = [];
			getProduct.forEach((item) => {
				item.setMemberSampleRequestItemResponseList.forEach((member) => {
					formattedList.push(member);
				});
			});
			setProductList(formattedList);
		}
		setSampleInfo(sampleViewStore?.response);
	}, [sampleViewStore]);

	const fetchAllTags = async (search) => {
		try {
			dispatch(openLoader());
			let params = '?type=PRODUCT_COLLECTION_TAGS';
			if (search) {
				params += `&search=${search}`;
			}
			const source = axios.CancelToken.source();
			previousCancel && previousCancel();
			previousCancel = source.cancel;

			const data = await fetchAllProductTags(params, source.token);
			previousCancel = null;

			setProductWiseTags(data);
			dispatch(closeLoader());
		} catch (error) {
			if (!axios.isCancel(error)) {
				dispatch(closeLoader());
			}
		}
	};
	const fetchSetInfos = async () => {
		try {
			dispatch(openLoader());
			const data = await getSetInfo(id);
			const products = await getSetProducts(id);
			setSetOrderInfo(data);
			setProductList(products);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const updateSetInfo = async () => {
		try {
			dispatch(openLoader());
			const data = await getSetInfo(id);
			setSetOrderInfo(data);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (initialDataLoaded && requestedDepartment !== 'SAMPLE_DEVELOPMENT') {
			(async () => {
				await fetchAllTags();
				await fetchSetInfos();
			})();
		}
	}, [initialDataLoaded]);

	useEffect(() => {
		let tmpProductList = productList.map((product) => {
			product.isSelected = selectedProductIds.includes(product.id);
			product.isAddedToList = !!selectedProductIds.length;
			return product;
		});
		setProductList(tmpProductList);
	}, [selectedProductIds]);

	const onUpdateBasicInfo = (product) => {
		let productList = productList?.map((item) => {
			if (item.id === product.id) {
				item = product;
			}
			return item;
		});
		setProductList(productList);
	};

	const onSuccessLikeProduct = () => {
		toastSuccess('Added to favourite');
	};

	const onLikeProduct = async (id) => {
		!isShowFavouriteFolderPopup && setIsShowFavouriteFolderPopup(true);
		await dispatch(likeProduct(id, onSuccessLikeProduct));
		setCurrentLikedStyle(id);
	};

	const onCloseViewModal = () => {
		dispatch(clearDesignViewModalInfo({}));
	};

	const onUpdateCostingStatus = (id) => {
		let productList = productList?.map((item) => {
			if (item.id === id) {
				item.costingCompleted = true;
			}
			return item;
		});
		setProductList(productList);
	};

	const onBasicInfoUpdateSuccess = (id) => {
		let orderedProductList = [];
		orderedProductList = productList?.map((product) => {
			if (product.id === id) {
				product.isBasicInfoUpdated = true;
				return {
					...product
				};
			} else return product;
		});
		setProductList(orderedProductList);
	};

	const setDesignIdToView = (product, openBasicInfo) => {
		dispatch(
			setDesignViewModalInfo({
				id: product.id,
				onCloseViewModal: (id) => onCloseViewModal(id),
				onUpdateCostingStatus: (id) => onUpdateCostingStatus(id),
				updateProductStatus: fetchSetInfos,
				source: 'COLLECTION_DETAILS',
				onBasicInfoUpdateSuccess: (id) => onBasicInfoUpdateSuccess(id),
				isDefaultOpenBasicInfo: openBasicInfo
			})
		);
	};

	const setDesignIdToViewFromProduct = (product, isFromOptions, openBasicInfo = false) => {
		if (!isFromOptions) {
			(product?.isBasicInfoUpdated || product.isSet) && setDesignIdToView(product);
		} else {
			setDesignIdToView(product, openBasicInfo);
		}
	};

	const onUpdateDesignImage = (id, docUrl) => {
		let productList = productList?.map((item) => {
			if (item.id === id) {
				item.designDocuments[0].docUrl = docUrl;
			}
			return item;
		});
		setProductList(productList);
	};

	const openEditSetOrder = () => {
		setIsOpenSetOrder(true);
	};

	const updateTagList = (payload) => {
		let tmpProductList = [...productList];
		payload.forEach((item) => {
			tmpProductList = tmpProductList?.map((product) => {
				if (product.id === item.id) {
					product.tagResponseList = item.tagResponseList;
				}
				return product;
			});
		});
		setProductList(tmpProductList);
	};

	const handleGoBackRoute = () => {
		let fromRoute = localStorage.getItem('fromRoute');
		if (fromRoute) {
			navigate(fromRoute);
		} else if (productList?.[0]?.collectionId) {
			navigate(`/collections/view/${productList?.[0]?.collectionId}`);
		} else {
			navigate(-1);
		}
	};

	const handleCloseBottomPopup = () => {
		setSelectedProduct([]);
		dispatch(storeProductData('selectedProductIds', []));
	};

	useEffect(() => {
		const selectedDesigns = productList.filter((product) => {
			if (selectedIds.includes(product.id)) {
				return product;
			}
		});
		setSelectedProduct(selectedDesigns);
	}, [selectedIds.length]);

	const renderTopBar = () => {
		return (
			<>
				<div className="top-header bg-white">
					<div className="filter-container-top pt-2 pb-2 bg-white">
						<div className="sample-full-status">
							<ul className="common-list-component">
								<li>
									<img
										src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
										alt="back"
										onClick={() => {
											navigate(-1);
										}}
										className="cursor-pointer"
									/>
									<span className="semibold-14 ml-2">
										{sampleInfo?.title || sampleInfo?.collectionName}
									</span>
									{sampleInfo?.refNo && (
										<span className="regular-12 gray_dark_02 weight-400">
											({sampleInfo?.refNo})
										</span>
									)}
								</li>
								{sampleInfo?.requestedBy && (
									<li>
										<span className="gray_dark_01">{sampleInfo?.requestedBy}</span>
									</li>
								)}
								{sampleInfo?.deliveryDate && (
									<li>
										<span>
											{changeDateFormat(
												sampleInfo?.deliveryDate,
												'YYYY-MM-DD',
												'DD MMM'
											)}
										</span>
									</li>
								)}
								<li>
									<span className="gray_dark_01">
										{checkSingular('Design', sampleInfo?.noOfDesign)}{' '}
									</span>
								</li>
								<li>
									<span>
										<span className="brand-common-design">{sampleInfo?.brand}</span>
									</span>
								</li>
							</ul>
						</div>
						<div className="filter-actions sample-dashboard-filter">
							<AddMemberToSample sampleId={sampleRequestId} />
						</div>
					</div>
				</div>
				{(sampleInfo?.description?.length > 0 || sampleInfo?.requestedDocumentList?.length > 0) && (
					<div style={{ margin: '12px 24px' }}>
						<TopDetails
							type={'SAMPLE'}
							description={sampleInfo?.description}
							documentLists={sampleInfo?.requestedDocumentList}
						/>
					</div>
				)}
			</>
		);
	};

	return (
		<>
			<div className="top-header collection-topbar">
				<div className="container-fluid pt-3 pl-0">
					{requestedDepartment !== 'SAMPLE_DEVELOPMENT' && (
						<div className="row">
							<div className="col-md-8">
								<div className="collection-overview d-flex align-items-center">
									<button className="button text size32 p-0" onClick={handleGoBackRoute}>
										<img
											src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
											alt="back"
										/>
									</button>
									<h1 className="semibold-14 m-0 ml-2">{setOrderInfo?.name || 'N/A'}</h1>
									<div className="ml-2">
										<span className="status set">Set</span>
									</div>
									{!requestedDepartment === 'SAMPLE_DEVELOPMENT' && (
										<div className="ml-3">
											<span
												className="regular-14 primary_bright_01 cursor-pointer"
												onClick={() => openEditSetOrder('SET')}
											>
												Edit
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					)}
					{requestedDepartment === 'SAMPLE_DEVELOPMENT' && renderTopBar()}
				</div>
			</div>
			<div className={`body-content pt-0 with-tab bg-white`}>
				<div className="collection-details-container d-flex">
					<div className="collection-details-data w-100">
						<div className="design-list-container mb-4 collection-details-design">
							<div className="container-fluid px-0">
								<section className="all-quotes-section px-0 py-0">
									{productList?.length === 0 && (
										<div className="alert alert-secondary text-center" role="alert">
											<p className="mb-4">No style found</p>
										</div>
									)}
								</section>
								<div
									className={`design-list-row pt-3 ${
										requestedDepartment !== 'SAMPLE_DEVELOPMENT' ? 'narrow-view' : ''
									}`}
								>
									{productList?.length > 0 &&
										productList?.map((product) => {
											return (
												<React.Fragment key={`product-${product.id}`}>
													<DesignCard
														productInfo={product}
														collectionId={id}
														onUpdateBasicInfo={(data) => onUpdateBasicInfo(data)}
														requestFrom={
															requestedDepartment
																? 'SAMPLE_DEVELOPMENT'
																: 'SET_ORDER_DETAILS'
														}
														setDesignIdToView={setDesignIdToViewFromProduct}
														onLikeProduct={onLikeProduct}
														onUpdateDesignImage={(id, docUrl) =>
															onUpdateDesignImage(id, docUrl)
														}
														onSetAllSelectFalse={() => false}
														onCreateOption={() => false}
														requestFromParam={''}
														isShowPrice={isShowPriceOnDesign()}
														productWiseTags={productWiseTags}
														updateTagList={updateTagList}
														handleBeforeUnload={() => false}
														setSelectedIds={setSelectedIds}
														fetchAllTags={fetchAllTags}
													/>
												</React.Fragment>
											);
										})}
								</div>
							</div>
						</div>
					</div>
				</div>
				{selectedProduct.length > 0 && (
					<SampleActionPopup
						selectedIds={selectedProduct}
						closeModal={handleCloseBottomPopup}
						idFromSetDetails={sampleRequestId}
					/>
				)}
			</div>
			{isOpenSetOrder && (
				<CreateSetOrder
					updateProductCard={updateSetInfo}
					requestType={'UPDATE'}
					selectedProduct={setOrderInfo}
					setOrderType={'SET'}
					onClose={() => {
						setIsOpenSetOrder(false);
					}}
				/>
			)}
			<div className={`${isShowFavouriteFolderPopup ? 'show' : 'hide'}`}>
				<SelectFolderPopup
					onClose={() => setIsShowFavouriteFolderPopup(false)}
					isShowCreateNew={isShowCreateNew}
					setIsShowCreateNew={setIsShowCreateNew}
					currentLikedStyle={currentLikedStyle}
				/>
			</div>
		</>
	);
};

export default SetOrderDetails;
